<script setup lang="ts">
import usePoolStore from '~~/app/store/pool'
import { FeedbackType } from '~~/interfaces/feedback'
import { IconChatAlt, IconBolt, IconSpeaker } from '#components'
import FilterButton from './FilterButton.vue'

const emits = defineEmits(['onClickComment', 'onClickGlobalScoreAverage'])

defineProps({
    isPartner: {
        type: Boolean,
        default: false
    }
})

const poolStore = usePoolStore()

const getCheckinsCount = computed<number>(
    () => poolStore.statistics?.checkinsCount || 0
)

const getReportsCount = computed<number>(
    () => poolStore.statistics?.reportsCount || 0
)

const getCommentsCount = computed<number>(
    () => poolStore.statistics?.commentsCount || 0
)

const getRatingsCount = computed<number>(
    () => poolStore.statistics?.ratingsCount || 0
)

const displayRatingCount = computed<boolean>(() => !getRatingsCount.value)

const poolRatingCanBeClicked = computed<boolean>(
    () =>
        !!(
            getRatingsCount.value !== 0 &&
            poolStore.hasAtLeastOneNewFeedback &&
            poolStore.getGlobalScoreAverage
        )
)

const isActiveFilterReport = computed<boolean>(
    () => poolStore.feedbacksFilter === FeedbackType.REPORT
)

const isActiveFilterComment = computed<boolean>(
    () => poolStore.feedbacksFilter === FeedbackType.COMMENT
)

const isActiveFilterCheckin = computed<boolean>(
    () => poolStore.feedbacksFilter === FeedbackType.CHECKIN
)

const onClickFilterCheckins = () => {
    if (getCheckinsCount.value) {
        poolStore.feedbacksFilter = isActiveFilterCheckin.value
            ? ''
            : FeedbackType.CHECKIN
    }
}

const onClickFilterComments = () => {
    if (getCommentsCount.value) {
        poolStore.feedbacksFilter = isActiveFilterComment.value
            ? ''
            : FeedbackType.COMMENT
    }
}

const onClickFilterReports = () => {
    if (getReportsCount.value) {
        poolStore.feedbacksFilter = isActiveFilterReport.value
            ? ''
            : FeedbackType.REPORT
    }
}

const onClickGlobalScoreAverage = () => {
    if (poolRatingCanBeClicked.value) {
        emits('onClickGlobalScoreAverage')
    }
}

watch(
    () => poolStore.feedbacksFilter,
    (value: string | null) => {
        poolStore.feedbacksMinIndex = 0

        if (value) {
            poolStore.filteredFeedbacks = []
        } else {
            poolStore.unfilteredFeedbacks = []
        }
        poolStore.fetchFeedbacksFromCommunityFeedbacks()
    }
)
</script>

<template>
    <div class="flex flex-col items-center gap-2 w-full">
        <span
            class="font-normal text-thirteen-exploitation leading-4 tracking-light text-gray-600"
        >
            {{ $t('period_info') }}
        </span>
        <div class="flex gap-2 justify-center w-full">
            <FilterButton
                icon-color="text-[#21c442]"
                :icon="IconBolt"
                :number="getCheckinsCount"
                :text-plural="$t('checkin.plural')"
                :text-singular="$t('checkin.singular')"
                :is-clicked="isActiveFilterCheckin"
                @click="onClickFilterCheckins"
            />
            <FilterButton
                icon-color="text-[#EF4444]"
                :icon="IconSpeaker"
                :number="getReportsCount"
                :text-plural="$t('report.plural')"
                :text-singular="$t('report.singular')"
                :is-clicked="isActiveFilterReport"
                @click="onClickFilterReports"
            />
            <FilterButton
                icon-color="text-gray-l-500"
                :icon="IconChatAlt"
                :number="getCommentsCount"
                :text-plural="$t('comment.plural')"
                :text-singular="$t('comment.singular')"
                :is-clicked="isActiveFilterComment"
                @click="onClickFilterComments"
            />
        </div>
        <div class="flex gap-2 w-full">
            <ChargemapB2CButton
                v-if="!isPartner"
                type="secondary"
                size="large"
                variant="blue"
                custom="align-center"
                class="border-gray-200 w-full"
                @click="emits('onClickComment')"
            >
                {{ $t('comment.verb') }}
            </ChargemapB2CButton>
            <PoolRating
                class="global-rating min-w-max min-h-[48px] border border-gray-200 rounded-lg justify-center px-4"
                :class="[
                    poolRatingCanBeClicked && 'cursor-pointer',
                    isPartner && 'flex-grow'
                ]"
                :display-rating-count
                :rating="poolStore.getGlobalScoreAverage"
                :rating-count="getRatingsCount"
                @click="onClickGlobalScoreAverage"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
.global-rating {
    :deep(.stars) {
        span {
            svg {
                @apply h-4 #{!important};
            }
        }
    }
}
</style>
